/**
 * Página principal de gestión de usuarios
 * Sistema Klever Octopus - Betel Natural
 */
import React, { useState } from 'react';
import {
  FiPlus, FiSearch, FiX, FiAlertTriangle, FiArrowUp,
  FiEdit, FiKey, FiTrash2, FiUsers, FiChevronLeft, FiChevronRight, FiEye
} from 'react-icons/fi';
import {
  useUsers,
  useAllRoles,
  useUserStats
} from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../components/UI/Notifications/NotificationProvider';
import { StatsCard } from '../../components/UI/StatsCard';
import { Modal } from '../../components/UI/Modal';
// ConfirmModal de la carpeta Modal/ (usa props onCancel/confirmClass); el de
// Modal.jsx espera onClose y dejaba el botón Cancelar sin efecto.
import ConfirmModal from '../../components/UI/Modal/ConfirmModal';
import { Loading } from '../../components/UI/Loading';
import userService from '../../services/userService';
import UserForm from './UserForm';
import UserPasswordModal from './UserPasswordModal';
import UserDetailModal from './UserDetailModal';

const Users = () => {
  const { user: currentUser, hasPermission } = useAuth();
  const { showSuccess, showError } = useNotifications();
  
  // Estados locales
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'

  // Hooks para datos
  const {
    users,
    loading,
    error,
    pagination,
    filters,
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    updateFilters,
    changePage,
    changeItemsPerPage,
    clearFilters,
    setError
  } = useUsers();

  const { roles } = useAllRoles();
  const { stats, loading: statsLoading, loadStats } = useUserStats();

  // Verificar permisos
  // Permiso RBAC del módulo de usuarios (superusuario pasa por wildcard '*')
  const canManageUsers = hasPermission('admin.users');
  const canCreate = canManageUsers;
  const canEdit = canManageUsers;
  const canDelete = canManageUsers;
  const canChangePassword = canManageUsers;
  const canView = canManageUsers;
  const canToggleStatus = canManageUsers;

  // Manejadores de eventos
  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalMode('create');
    setShowUserModal(true);
  };

  const handleEditUser = async (user) => {
    // Cargar datos completos del usuario
    try {
      const response = await userService.getUserById(user.id);

      if (response.success) {
        setSelectedUser(response.data);
        setModalMode('edit');
        setShowUserModal(true);
      } else {
        setError(response.message || 'Error al cargar datos del usuario');
      }
    } catch (error) {
      console.error('Error loading user details:', error);
      setError('Error al cargar datos del usuario');
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
  };

  const handleViewUser = async (user) => {
    // Cargar datos completos (roles asignados, datos de vendedor, último acceso, etc.)
    try {
      const response = await userService.getUserById(user.id);
      if (response.success) {
        setSelectedUser(response.data);
        setShowDetailModal(true);
      } else {
        setError(response.message || 'Error al cargar datos del usuario');
      }
    } catch (error) {
      console.error('Error loading user details:', error);
      setError('Error al cargar datos del usuario');
    }
  };

  const handleEditFromDetail = () => {
    // selectedUser ya tiene los datos completos cargados en handleViewUser
    setShowDetailModal(false);
    setModalMode('edit');
    setShowUserModal(true);
  };

  const handleToggleStatus = (user) => {
    setSelectedUser(user);
    setShowStatusModal(true);
  };

  const confirmToggleStatus = async () => {
    const wasActive = selectedUser.is_active;
    const result = await toggleUserStatus(selectedUser.id, !selectedUser.is_active);
    setShowStatusModal(false);
    if (result.success) {
      showSuccess(wasActive ? 'Usuario desactivado' : 'Usuario activado');
      setSelectedUser(null);
      loadStats(); // refrescar contadores Total/Activos/Inactivos al momento
    } else {
      showError(result.message || 'Error al cambiar el estado del usuario');
    }
  };

  const handleFormSubmit = async (userData) => {
    let result;
    
    if (modalMode === 'create') {
      result = await createUser(userData);
    } else {
      result = await updateUser(selectedUser.id, userData);
    }

    if (result.success) {
      showSuccess(modalMode === 'create' ? 'Usuario creado correctamente' : 'Usuario actualizado correctamente');
      setShowUserModal(false);
      setSelectedUser(null);
      loadStats(); // refrescar contadores
    } else {
      // Los errores se manejan en el hook
      return result;
    }
  };

  const confirmDelete = async () => {
    const result = await deleteUser(selectedUser.id);

    if (result.success) {
      showSuccess('Usuario eliminado correctamente');
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadStats(); // refrescar contadores
    } else {
      showError(result.message || 'Error al eliminar el usuario');
      setShowDeleteModal(false);
    }
  };

  const handleSearch = (searchTerm) => {
    updateFilters({ search: searchTerm });
  };

  const handleRoleFilter = (role) => {
    updateFilters({ role });
  };

  const handleStatusFilter = (status) => {
    updateFilters({ status });
  };

  const handleSort = (field) => {
    const newOrder = filters.sort_by === field && filters.sort_order === 'asc' ? 'desc' : 'asc';
    updateFilters({ sort_by: field, sort_order: newOrder });
  };

  if (loading && users.length === 0) {
    return <Loading message="Cargando usuarios..." />;
  }

  return (
    <div className="users-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">Gestión de Usuarios</h1>
          <p className="page-description">
            Administra usuarios, roles y permisos del sistema
          </p>
        </div>
        
        {canCreate && (
          <button 
            className="btn btn-primary"
            onClick={handleCreateUser}
          >
            <FiPlus />
            Nuevo Usuario
          </button>
        )}
      </div>

      {/* Estadísticas */}
      {!statsLoading && stats && (
        <div className="stats-container">
          <div className="stats-grid">
            <StatsCard
              title="Total Usuarios"
              value={stats.total || 0}
              variant="primary"
            />
            <StatsCard
              title="Usuarios Activos"
              value={stats.active || 0}
              variant="success"
            />
            <StatsCard
              title="Usuarios Inactivos"
              value={stats.inactive || 0}
              variant="warning"
            />
            <StatsCard
              title="Roles Configurados"
              value={roles.length}
              variant="info"
            />
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="filters-section">
        <div className="filters-row">
          {/* Búsqueda */}
          <div className="search-field">
            <FiSearch />
            <input
              type="text"
              placeholder="Buscar por nombre o email (mín. 3 caracteres)"
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              className="form-control"
            />
          </div>

          {/* Filtro por rol */}
          <select
            value={filters.role}
            onChange={(e) => handleRoleFilter(e.target.value)}
            className="form-control"
          >
            <option value="">Todos los roles</option>
            {roles.map((role, index) => (
              <option key={role.id || index} value={role.id}>
                {role.display_name || role.name}
              </option>
            ))}
          </select>

          {/* Filtro por estado */}
          <select
            value={filters.status}
            onChange={(e) => handleStatusFilter(e.target.value)}
            className="form-control"
          >
            <option value="">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>

          {/* Limpiar filtros */}
          <button 
            className="btn btn-outline-secondary"
            onClick={clearFilters}
          >
            <FiX />
            Limpiar
          </button>
        </div>
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="alert alert-error">
          <FiAlertTriangle />
          {error}
          <button 
            className="btn btn-link"
            onClick={() => setError(null)}
          >
            <FiX />
          </button>
        </div>
      )}

      {/* Tabla de usuarios */}
      <div className="table-container">
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th 
                  className={`sortable ${filters.sort_by === 'nombre' ? filters.sort_order : ''}`}
                  onClick={() => handleSort('nombre')}
                >
                  Usuario
                  <FiArrowUp />
                </th>
                <th 
                  className={`sortable ${filters.sort_by === 'email' ? filters.sort_order : ''}`}
                  onClick={() => handleSort('email')}
                >
                  Email
                  <FiArrowUp />
                </th>
                <th>Rol</th>
                <th 
                  className={`sortable ${filters.sort_by === 'is_active' ? filters.sort_order : ''}`}
                  onClick={() => handleSort('is_active')}
                >
                  Estado
                  <FiArrowUp />
                </th>
                <th 
                  className={`sortable ${filters.sort_by === 'created_at' ? filters.sort_order : ''}`}
                  onClick={() => handleSort('created_at')}
                >
                  Fecha Creación
                  <FiArrowUp />
                </th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                // Obtener el rol: primero intentar assigned_roles (RBAC), luego role (legacy)
                const userRole = user.assigned_roles && user.assigned_roles.length > 0
                  ? user.assigned_roles[0]
                  : null;

                // Obtener el nombre completo
                const fullName = user.full_name || `${user.nombre || ''} ${user.apellido || ''}`.trim() || 'Usuario';

                // El backend no permite que un usuario cambie su propio estado
                const canToggle = canToggleStatus && user.id !== currentUser?.id;

                return (
                  <tr key={user.id}>
                    <td>
                      <div className="user-info">
                        <div className="user-details">
                          <span className="user-name">
                            {fullName}
                          </span>
                          <small className="user-id">ID: {user.id}</small>
                        </div>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <span
                        className={`role-badge role-${userRole?.name || 'default'}`}
                        style={{ backgroundColor: userRole?.color || '#6c757d' }}
                      >
                        {userRole?.display_name || userRole?.name || 'Sin rol'}
                      </span>
                    </td>
                  <td>
                    <span
                      className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}
                      onClick={canToggle ? () => handleToggleStatus(user) : undefined}
                      onKeyDown={canToggle ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleToggleStatus(user);
                        }
                      } : undefined}
                      role={canToggle ? 'button' : undefined}
                      tabIndex={canToggle ? 0 : undefined}
                      title={canToggle ? (user.is_active ? 'Clic para desactivar' : 'Clic para activar') : undefined}
                      style={canToggle ? { cursor: 'pointer' } : undefined}
                    >
                      <i className={`fas fa-circle ${user.is_active ? 'text-success' : 'text-danger'}`}></i>
                      {user.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td>
                    <div className="action-buttons" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      {canView && (
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleViewUser(user)}
                          title="Ver detalle"
                          style={{
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '6px',
                            width: '36px',
                            height: '36px'
                          }}
                        >
                          <FiEye size={18} />
                        </button>
                      )}

                      {canEdit && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleEditUser(user)}
                          title="Editar usuario"
                          style={{
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '6px',
                            width: '36px',
                            height: '36px'
                          }}
                        >
                          <FiEdit size={18} />
                        </button>
                      )}

                      {canChangePassword && (
                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => handleChangePassword(user)}
                          title="Cambiar contraseña"
                          style={{
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '6px',
                            width: '36px',
                            height: '36px'
                          }}
                        >
                          <FiKey size={18} />
                        </button>
                      )}

                      {canDelete && user.id !== currentUser?.id && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteUser(user)}
                          title="Eliminar usuario"
                          style={{
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '6px',
                            width: '36px',
                            height: '36px'
                          }}
                        >
                          <FiTrash2 size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          {users.length === 0 && !loading && (
            <div className="empty-state">
              <FiUsers />
              <h3>No se encontraron usuarios</h3>
              <p>No hay usuarios que coincidan con los filtros aplicados.</p>
              {canCreate && (
                <button 
                  className="btn btn-primary"
                  onClick={handleCreateUser}
                >
                  Crear primer usuario
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Paginación */}
      {pagination.totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Mostrando {users.length} de {pagination.totalItems} usuarios
          </div>
          
          <div className="pagination-controls">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => changePage(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
            >
              <FiChevronLeft />
              Anterior
            </button>

            <span className="pagination-info">
              Página {pagination.currentPage} de {pagination.totalPages}
            </span>

            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => changePage(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              Siguiente
              <FiChevronRight />
            </button>
          </div>

          <select
            value={pagination.itemsPerPage}
            onChange={(e) => changeItemsPerPage(parseInt(e.target.value))}
            className="form-control pagination-select"
          >
            <option value={10}>10 por página</option>
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </select>
        </div>
      )}

      {/* Modales */}
      {showUserModal && (
        <Modal
          title={modalMode === 'create' ? 'Crear Usuario' : 'Editar Usuario'}
          isOpen={showUserModal}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
          size="large"
        >
          <UserForm
            user={selectedUser}
            mode={modalMode}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowUserModal(false);
              setSelectedUser(null);
            }}
          />
        </Modal>
      )}

      {showPasswordModal && selectedUser && (
        <UserPasswordModal
          user={selectedUser}
          isOpen={showPasswordModal}
          onClose={() => {
            setShowPasswordModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            showSuccess('Contraseña actualizada correctamente');
            setShowPasswordModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {showDeleteModal && selectedUser && (
        <ConfirmModal
          isOpen={showDeleteModal}
          title="Confirmar eliminación"
          message={`¿Estás seguro de que deseas eliminar al usuario "${selectedUser.full_name || `${selectedUser.nombre || ''} ${selectedUser.apellido || ''}`.trim() || selectedUser.email}"?`}
          confirmText="Eliminar"
          confirmClass="btn-danger"
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {showDetailModal && selectedUser && (
        <UserDetailModal
          user={selectedUser}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedUser(null);
          }}
          onEdit={canEdit ? handleEditFromDetail : null}
        />
      )}

      {showStatusModal && selectedUser && (
        <ConfirmModal
          isOpen={showStatusModal}
          title={selectedUser.is_active ? 'Desactivar usuario' : 'Activar usuario'}
          message={`¿Deseas ${selectedUser.is_active ? 'desactivar' : 'activar'} al usuario "${selectedUser.full_name || `${selectedUser.nombre || ''} ${selectedUser.apellido || ''}`.trim() || selectedUser.email}"?`}
          confirmText={selectedUser.is_active ? 'Desactivar' : 'Activar'}
          confirmClass={selectedUser.is_active ? 'btn-warning' : 'btn-success'}
          onConfirm={confirmToggleStatus}
          onCancel={() => {
            setShowStatusModal(false);
            setSelectedUser(null);
          }}
        />
      )}

    </div>
  );
};

export default Users;
